import { useState, useCallback } from 'react';
import {
  ValidationRule,
  ValidationResult,
  validateField,
} from '../utils/validationUtils';

export interface UseValidationOptions {
  [fieldName: string]: ValidationRule[];
}

export interface UseValidationReturn {
  values: { [key: string]: string };
  errors: { [key: string]: string };
  touched: { [key: string]: boolean };
  isValid: boolean;
  isDirty: boolean;
  setValue: (field: string, value: string) => void;
  setTouched: (field: string, touched?: boolean) => void;
  validateField: (field: string) => ValidationResult;
  validateAll: () => boolean;
  reset: () => void;
  getFieldProps: (field: string) => {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    error: string;
    'aria-describedby': string | undefined;
  };
}

/**
 * Hook personalizado para gerenciar validações de formulário
 * @param validationRules - Regras de validação para cada campo
 * @param initialValues - Valores iniciais dos campos
 */
export const useValidation = (
  validationRules: UseValidationOptions,
  initialValues: { [key: string]: string } = {}
): UseValidationReturn => {
  // Estado dos valores dos campos
  const [values, setValues] = useState<{ [key: string]: string }>(initialValues);

  // Estado dos erros de validação
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Estado dos campos que foram tocados (focus/blur)
  const [touched, setTouchedState] = useState<{ [key: string]: boolean }>({});

  // Função para definir o valor de um campo
  const setValue = useCallback((field: string, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));

    // Limpar erro quando usuário começa a digitar (se houver erro)
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Função para marcar campo como tocado
  const setTouched = useCallback((field: string, touchedValue: boolean = true) => {
    setTouchedState(prev => ({ ...prev, [field]: touchedValue }));

    // Validar campo quando ele perde o foco
    if (touchedValue) {
      const result = validateField(values[field] || '', validationRules[field] || []);
      setErrors(prev => ({
        ...prev,
        [field]: result.isValid ? '' : result.errorMessage || 'Campo inválido'
      }));
    }
  }, [values, validationRules]);

  // Função para validar um campo específico
  const validateSingleField = useCallback((field: string): ValidationResult => {
    const rules = validationRules[field] || [];
    const value = values[field] || '';
    return validateField(value, rules);
  }, [values, validationRules]);

  // Função para validar todos os campos
  const validateAll = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};
    let allValid = true;

    Object.keys(validationRules).forEach(field => {
      const result = validateSingleField(field);
      if (!result.isValid) {
        newErrors[field] = result.errorMessage || 'Campo inválido';
        allValid = false;
      }
    });

    setErrors(newErrors);
    // Marcar todos os campos como tocados
    const allTouched = Object.keys(validationRules).reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {}
    );
    setTouchedState(allTouched);

    return allValid;
  }, [validationRules, validateSingleField]);

  // Função para resetar o formulário
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouchedState({});
  }, [initialValues]);

  // Propriedades para conectar com componentes de input
  const getFieldProps = useCallback((field: string) => ({
    value: values[field] || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setValue(field, e.target.value),
    onBlur: () => setTouched(field, true),
    error: errors[field] || '',
    'aria-describedby': errors[field] ? `${field}-error` : undefined,
    'aria-invalid': errors[field] ? 'true' : undefined,
  }), [values, errors, setValue, setTouched]);

  // Verificar se o formulário é válido
  const isValid = Object.values(errors).every(error => !error) &&
                  Object.keys(validationRules).every(field => {
                    const result = validateSingleField(field);
                    return result.isValid;
                  });

  // Verificar se algum campo foi modificado
  const isDirty = Object.keys(values).some(key =>
    values[key] !== (initialValues[key] || '')
  );

  return {
    values,
    errors,
    touched,
    isValid,
    isDirty,
    setValue,
    setTouched,
    validateField: validateSingleField,
    validateAll,
    reset,
    getFieldProps,
  };
};